import React, { useState } from 'react';
import { User, Mail, Phone, Lock, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from 'hooks/useLanguage';
import Input from 'components/ui/Input';

const PersonalInfoStep = ({ formData, onInputChange, error }) => {
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, text: '', color: 'gray' };
    
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/[0-9]/?.test(password)) strength++;
    if (/[^A-Za-z0-9]/?.test(password)) strength++;

    const levels = [
      { text: t('Very Weak'), color: 'red' },
      { text: t('Weak'), color: 'orange' },
      { text: t('Fair'), color: 'yellow' },
      { text: t('Good'), color: 'blue' },
      { text: t('Strong'), color: 'green' }
    ];

    return { strength, ...levels?.[Math.min(strength, 4)] };
  };

  const passwordStrength = getPasswordStrength(formData?.password);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('Personal Information')}
        </h2>
        <p className="text-gray-600">
          {t('Please provide your basic information to create your account')}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* First Name */}
        <div className="space-y-2">
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            {t('First Name')} *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              id="firstName"
              name="firstName"
              type="text"
              required
              value={formData?.firstName}
              onChange={(e) => onInputChange('firstName', e?.target?.value)}
              className="pl-10"
              placeholder={t('Enter your first name')}
            />
          </div>
        </div>

        {/* Last Name */}
        <div className="space-y-2">
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            {t('Last Name')} *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              id="lastName"
              name="lastName"
              type="text"
              required
              value={formData?.lastName}
              onChange={(e) => onInputChange('lastName', e?.target?.value)}
              className="pl-10"
              placeholder={t('Enter your last name')}
            />
          </div>
        </div>
      </div>
      {/* Email */}
      <div className="space-y-2">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          {t('Email Address')} *
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData?.email}
            onChange={(e) => onInputChange('email', e?.target?.value)}
            className="pl-10"
            placeholder={t('Enter your email address')}
          />
        </div>
      </div>
      {/* Phone */}
      <div className="space-y-2">
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
          {t('Phone Number')} *
        </label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData?.phone}
            onChange={(e) => onInputChange('phone', e?.target?.value)}
            className="pl-10"
            placeholder={t('Enter your phone number')}
          />
        </div>
      </div>
      {/* Password */}
      <div className="space-y-2">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
          {t('Password')} *
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={formData?.password}
            onChange={(e) => onInputChange('password', e?.target?.value)}
            className="pl-10 pr-10"
            placeholder={t('Create a strong password')}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        
        {/* Password Strength Indicator */}
        {formData?.password && (
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 bg-${passwordStrength?.color}-500`}
                  style={{ width: `${(passwordStrength?.strength / 5) * 100}%` }}
                />
              </div>
              <span className={`text-xs font-medium text-${passwordStrength?.color}-600`}>
                {passwordStrength?.text}
              </span>
            </div>
            <ul className="text-xs text-gray-600 space-y-1">
              <li className={formData?.password?.length >= 8 ? 'text-green-600' : ''}>
                • {t('At least 8 characters')}
              </li>
              <li className={/[A-Z]/?.test(formData?.password) ? 'text-green-600' : ''}>
                • {t('One uppercase letter')}
              </li>
              <li className={/[0-9]/?.test(formData?.password) ? 'text-green-600' : ''}>
                • {t('One number')}
              </li>
              <li className={/[^A-Za-z0-9]/?.test(formData?.password) ? 'text-green-600' : ''}>
                • {t('One special character')}
              </li>
            </ul>
          </div>
        )}
      </div>
      {/* Confirm Password */}
      <div className="space-y-2">
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
          {t('Confirm Password')} *
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={formData?.confirmPassword}
            onChange={(e) => onInputChange('confirmPassword', e?.target?.value)}
            className="pl-10 pr-10"
            placeholder={t('Confirm your password')}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        
        {/* Password Match Indicator */}
        {formData?.confirmPassword && (
          <div className={`text-xs mt-1 ${
            formData?.password === formData?.confirmPassword ? 'text-green-600' : 'text-red-600'
          }`}>
            {formData?.password === formData?.confirmPassword
              ? t('Passwords match')
              : t('Passwords do not match')
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalInfoStep;